/**
 * risk/riskEngine.ts
 *
 * Lightweight pre-trade risk quality engine for MER Algo Footprints v4.2+
 *
 * This is NOT an execution engine. It evaluates the quality of a potential
 * trade setup before the trader acts on it — helping separate:
 *   interesting → good → ready → urgent
 *
 * Inputs: a TradeSetup + current SymbolContext + optional AI thesis conviction
 * Outputs: RiskAssessment with R calculation, stop quality, readiness tier
 *
 * Philosophy:
 * - Never invent precision from incomplete data
 * - Expose uncertainty explicitly
 * - Conservative defaults (if unsure → lower score)
 * - No execution — pure pre-trade quality control
 */

import { logReadinessUpgrade } from '../../../diagnostics/diagnosticsStore';
import { TradeSetup, RegimeResult, LiquidityZone, MarketPhase, FairValueGap } from '../../../types';
import { SymbolContext } from '../../../services/buildSymbolContext';

// ─── Readiness tier ───────────────────────────────────────────────────────────

export type ReadinessTier =
  | 'interesting'   // signal present but not actionable yet
  | 'good'          // setup is developing, waiting for confirmation
  | 'ready'         // all conditions met — executable
  | 'urgent';       // high-conviction, time-sensitive opportunity

export const READINESS_COLORS: Record<ReadinessTier, string> = {
  interesting: 'var(--blue-dim)',
  good:        'var(--blue)',
  ready:       'var(--green)',
  urgent:      'var(--gold)',
};

export const READINESS_LABELS: Record<ReadinessTier, string> = {
  interesting: '◎ INTERESTING',
  good:        '◑ GOOD',
  ready:       '● READY',
  urgent:      '⚡ URGENT',
};

// ─── Risk assessment output ───────────────────────────────────────────────────

export interface RiskAssessment {
  // Core metrics
  expectedR:       number | null;   // reward-to-risk ratio (null if levels incomplete)
  stopQuality:     number;          // 0–100 — how clean the stop placement is
  targetQuality:   number;          // 0–100 — how achievable/clean the target is
  setupCleanliness: number;         // 0–100 — overall setup quality score

  // Readiness
  readiness:       ReadinessTier;
  readinessScore:  number;          // 0–100 underlying score

  // Flags
  hasCompleteLevel:  boolean;       // entry + stop + target all defined
  trapConflict:      boolean;       // active high-severity trap in same direction
  regimeConflict:    boolean;       // regime suggests opposite direction
  premiumDiscountConflict: boolean; // price in wrong zone for this direction

  // Confidence
  confluenceCount: number;          // number of confirming signals
  uncertaintyNote: string;          // plain-English warning about limitations

  // What's missing for upgrade
  missingForReady:  string[];       // what would move this to 'ready'
  missingForUrgent: string[];       // what would move this to 'urgent'
}

// ─── Main evaluator ───────────────────────────────────────────────────────────

export function evaluateSetupRisk(
  setup:       TradeSetup,
  ctx:         SymbolContext,
  aiConviction: number = 0   // 0–100 from AI thesis, 0 if unavailable
): RiskAssessment {
  const curPrice = ctx.curPrice;

  // ── Resolve numeric levels: structured fields first, regex fallback ────────
  // setupEngine now populates entryPrice/stopPrice/target1Price directly.
  // extractFirstNumber fallback handles any setups that predate the structured fields.
  const entryNum    = setup.entryPrice   ?? extractFirstNumber(setup.entry);
  const stopNum     = setup.stopPrice    ?? extractFirstNumber(setup.invalidation);
  const target1Num  = setup.target1Price ?? extractFirstNumber(setup.t1);
  const target2Num  = setup.target2Price ?? extractFirstNumber(setup.t2);

  const hasCompleteLevel = entryNum !== null && stopNum !== null && target1Num !== null;

  // ── R calculation ────────────────────────────────────────────────────────
  let expectedR: number | null = null;
  if (hasCompleteLevel && entryNum !== null && stopNum !== null && target1Num !== null) {
    const riskPerUnit   = Math.abs(entryNum - stopNum);
    const rewardPerUnit = Math.abs(target1Num - entryNum);
    if (riskPerUnit > 0) {
      expectedR = rewardPerUnit / riskPerUnit;
    }
  }

  // ── Stop quality ─────────────────────────────────────────────────────────
  // Good stops are: below a structural level, defined, not too wide, not too tight
  let stopQuality = 50; // baseline
  if (!stopNum) {
    stopQuality = 0;
  } else {
    const stopDistPct = stopNum !== null ? Math.abs(curPrice - stopNum) / curPrice * 100 : 0;

    // Too tight (< 0.2%) or too wide (> 3%) reduces quality
    if (stopDistPct < 0.2) stopQuality -= 20;
    else if (stopDistPct < 0.4) stopQuality += 10;
    else if (stopDistPct > 3.0) stopQuality -= 15;
    else if (stopDistPct <= 2.0) stopQuality += 20;

    // Stop near a STRUCTURAL level increases quality — generic reference levels don't count
    const nearStructural = ctx.zones.some(z => {
      if (Math.abs(z.price - stopNum) / stopNum >= 0.003) return false;
      const cat = (z as LiquidityZone & { category?: string }).category;
      return cat === 'structural_liquidity' || cat === 'gamma_level' || cat === undefined;
    });
    if (nearStructural) stopQuality += 20;
    else {
      // Near a non-structural level — partial credit only
      const nearAnyLevel = ctx.zones.some(z => Math.abs(z.price - stopNum) / stopNum < 0.003 && z.sig === 'High');
      if (nearAnyLevel) stopQuality += 8;
    }

    // Stop behind SMC structure: only award bonus for MAJOR + recent structure
    // Weak/minor/stale structure does not justify a stop quality boost
    const _bosStrong  = !!(ctx.smc?.lastBOS   && ctx.smc.lastBOS.strength   === 'major'
                          && ctx.smc.lastBOS.candleIdx   >= ctx.candles.length - 12);
    const _chochStrong= !!(ctx.smc?.lastCHOCH && ctx.smc.lastCHOCH.strength === 'major'
                          && ctx.smc.lastCHOCH.candleIdx >= ctx.candles.length - 12);
    if (_bosStrong || _chochStrong) stopQuality += 10;
    // Minor/stale structure: no boost (was previously awarding +10 unconditionally)

    stopQuality = Math.min(100, Math.max(0, stopQuality));
  }

  // ── Target quality ───────────────────────────────────────────────────────
  let targetQuality = 50;
  if (!target1Num) {
    targetQuality = 20; // partial credit — some target logic
  } else {
    // R > 2 is excellent, R < 1 is poor
    if (expectedR !== null) {
      if (expectedR >= 3.0) targetQuality = 90;
      else if (expectedR >= 2.0) targetQuality = 75;
      else if (expectedR >= 1.5) targetQuality = 60;
      else if (expectedR >= 1.0) targetQuality = 45;
      else targetQuality = 20;
    }

    // Target near resistance/support
    const dir = setup.dir;
    const nearTargetLevel = ctx.zones.some(z =>
      Math.abs(z.price - target1Num) / target1Num < 0.005 &&
      ((dir === 'long' && z.type === 'resistance') || (dir === 'short' && z.type === 'support'))
    );
    if (nearTargetLevel) targetQuality = Math.min(100, targetQuality + 15);

    // Second target available
    if (target2Num) targetQuality = Math.min(100, targetQuality + 10);
  }

  // ── Conflict detection ───────────────────────────────────────────────────
  const dir = setup.dir;

  // Trap conflict: zone-aware, consistent with setupEngine precision standard.
  // Uses setup.entryPrice / stopPrice (already resolved above) as the reference zone.
  // Only falls back to curPrice when no structured levels are available.
  const _trapTol = 0.018; // 1.8% proximity window — mirrors setupEngine
  const trapConflict = ctx.traps.some(t => {
    if (t.severity !== 'High') return false;
    const sideMatch = (dir === 'long'  && t.side === 'bull') ||
                      (dir === 'short' && t.side === 'bear');
    if (!sideMatch) return false;
    if (t.level == null) return true; // no level = always relevant

    // Primary: is the trap level near the setup entry zone?
    if (entryNum != null && Math.abs(t.level - entryNum) / entryNum < _trapTol) return true;
    // Secondary: is the trap level near the setup stop/invalidation zone?
    if (stopNum  != null && Math.abs(t.level - stopNum)  / stopNum  < _trapTol) return true;
    // Fallback: only use current price when no structured levels exist
    if (entryNum == null && stopNum == null) {
      return Math.abs(t.level - curPrice) / curPrice < 0.015;
    }
    return false; // trap is away from the setup zone — not a real conflict
  });

  const regimeConflict = (() => {
    if (!ctx.regime) return false;
    const dom = ctx.regime.dominant;
    if (dir === 'long'  && dom === 'meanReversion' && ctx.regime.vwapDist > 0.5) return true;
    if (dir === 'short' && dom === 'meanReversion' && ctx.regime.vwapDist < -0.5) return true;
    if (dir === 'long'  && dom === 'noTrade') return true;
    if (dir === 'short' && dom === 'noTrade') return true;
    return false;
  })();

  const premiumDiscountConflict = (() => {
    const pd = ctx.smc?.premiumDiscount;
    if (!pd) return false;
    if (dir === 'long'  && pd.zone === 'premium')  return true;
    if (dir === 'short' && pd.zone === 'discount')  return true;
    return false;
  })();

  // ── Confluence count — POSITIVE evidence only ──────────────────────────────
  // Absence of conflict is NOT confluence (removed: !trapConflict, !regimeConflict,
  // !premiumDiscountConflict — these are neutral, not positive signals).
  let confluenceCount = 0;
  if (ctx.smc?.bias === (dir === 'long' ? 'bullish' : 'bearish')) confluenceCount++;
  // sweepAndReverse now requires displacement — worth more than before
  if (ctx.smc?.sweepAndReverse && ctx.smc?.biasStrength === 'major') confluenceCount++;
  else if (ctx.smc?.sweepAndReverse) confluenceCount += 0.5; // partial credit for minor sweep
  if (ctx.regime?.dominant === 'breakout' || ctx.regime?.dominant === 'trend') confluenceCount++;
  if ((ctx.regime?.rvol ?? 0) > 1.3) confluenceCount++;
  if (aiConviction >= 65) confluenceCount++;
  if (expectedR !== null && expectedR >= 2.0) confluenceCount++;
  if (stopQuality >= 70) confluenceCount++;
  // Premium/discount: discount for longs / premium for shorts is a positive, not neutral
  if (!premiumDiscountConflict && ctx.smc?.premiumDiscount) {
    const pd = ctx.smc.premiumDiscount;
    if ((dir === 'long' && pd.zone === 'discount') || (dir === 'short' && pd.zone === 'premium')) {
      confluenceCount++; // aligned location is positive evidence
    }
  }
  confluenceCount = Math.round(confluenceCount); // normalize half-credits

  // ── Setup cleanliness — cap direct confidence inheritance ───────────────────
  // Setup engine confidence is already somewhat conservative; apply 90% scaling
  // so high-confidence setups don't automatically pass into high cleanliness.
  let setupCleanliness = Math.round(setup.confidence * 0.90);
  if (trapConflict)            setupCleanliness -= 20;
  if (regimeConflict)          setupCleanliness -= 15;
  if (premiumDiscountConflict) setupCleanliness -= 10;
  if (!hasCompleteLevel)       setupCleanliness -= 15;
  if (confluenceCount >= 6)    setupCleanliness = Math.min(100, setupCleanliness + 10);
  setupCleanliness = Math.min(100, Math.max(0, setupCleanliness));

  // ── Phase / scenario alignment (additive adjustments) ──────────────────────
  type RegimeWithPhase = RegimeResult & { phase?: MarketPhase };
  const _phase     = (ctx.regime as RegimeWithPhase | null)?.phase ?? 'RANGE';
  const _scenario  = ctx.smc?.scenarioType ?? 'none';
  const _dispDir   = ctx.smc?.displacementDirection ?? 'none';
  const _dispFresh = (ctx.smc?.displacementFreshness ?? 0) >= 0.4;
  const _dispStr   = ctx.smc?.displacementStrength ?? 0;
  const _seqConf   = ctx.smc?.liquiditySequenceConfidence ?? 0;
  const _expNextLiq = ctx.smc?.expectedNextLiquidity ?? null;

  const setupIsContinuation = setup.type.startsWith('Continuation');
  const setupIsReversal     = setup.type.includes('Reversal') || setup.type.includes('CHOCH') ||
                              setup.type.includes('Sweep &') || setup.type.includes('Fade');

  // Aligned with phase
  const phaseAligned =
    (_phase === 'EXPANSION'     && setupIsContinuation && _dispDir !== 'none') ||
    (_phase === 'POST_EXPANSION' && setupIsContinuation) ||
    (_phase === 'RANGE'          && !setupIsContinuation);

  // Scenario alignment
  const scenarioAligned =
    (_scenario === 'continuation' && setupIsContinuation) ||
    (_scenario === 'reversal'     && setupIsReversal);

  // Displacement conflict: reversal setup fighting fresh strong displacement
  const displacementConflict = setupIsReversal && _dispFresh && _dispStr >= 0.4 &&
    _dispDir !== 'none' &&
    ((dir === 'short' && _dispDir === 'bullish') ||
     (dir === 'long'  && _dispDir === 'bearish'));

  // Liquidity sequence alignment
  const liqSeqAligned = _expNextLiq !== null && _seqConf >= 0.4 &&
    ((dir === 'long'  && _expNextLiq === 'buy_side') ||
     (dir === 'short' && _expNextLiq === 'sell_side'));

  // Apply to cleanliness
  if (phaseAligned)          setupCleanliness = Math.min(100, setupCleanliness + 6);
  if (scenarioAligned)       setupCleanliness = Math.min(100, setupCleanliness + 5);
  if (displacementConflict)  setupCleanliness = Math.max(0,   setupCleanliness - 18);
  if (liqSeqAligned)         setupCleanliness = Math.min(100, setupCleanliness + 4);

  // ── Trend conflict and stacked conflict penalties ─────────────────────────
  const _trend     = ctx.trend;
  const _tDir      = _trend?.trendDirection  ?? 'neutral';
  const _tStr      = _trend?.trendStrength   ?? 0;
  const _tState    = _trend?.trendState      ?? 'transition';
  const _tInTrend  = _tState === 'trend' || _tState === 'pullback';
  const _tCompressed = _tState === 'compression' || _tState === 'transition';

  // Trend conflict: setup direction conflicts with established EMA trend
  const trendConflict = _tInTrend && _tStr >= 0.35 && _tDir !== 'neutral' &&
    ((dir === 'long'  && _tDir === 'bearish') ||
     (dir === 'short' && _tDir === 'bullish'));

  // Trend alignment: setup direction confirmed by EMA trend
  const trendAligned = _tInTrend && _tStr >= 0.35 && _tDir !== 'neutral' &&
    ((dir === 'long'  && _tDir === 'bullish') ||
     (dir === 'short' && _tDir === 'bearish'));

  // Stacked conflict: fights BOTH fresh displacement AND strong EMA trend
  // Stronger penalty than either alone
  const stackedConflict = displacementConflict && trendConflict;

  if (stackedConflict) {
    // Fighting BOTH displacement and trend — significantly stronger than either alone
    setupCleanliness = Math.max(0, setupCleanliness - 30);  // was -20
  } else if (displacementConflict) {
    setupCleanliness = Math.max(0, setupCleanliness - 18);  // unchanged
  } else if (trendConflict && !_tCompressed) {
    setupCleanliness = Math.max(0, setupCleanliness - 10);  // unchanged
  }

  // Quad-alignment bonus: phase + displacement + trend + liquidity sequence all agree
  const _dispAligned = _dispFresh && _dispDir !== 'none' &&
    ((dir === 'long'  && _dispDir === 'bullish') ||
     (dir === 'short' && _dispDir === 'bearish'));
  const quadAligned = phaseAligned && _dispAligned && trendAligned && liqSeqAligned;
  if (quadAligned) {
    setupCleanliness = Math.min(100, setupCleanliness + 12); // all four pillars — strong bonus
  } else if (trendAligned && phaseAligned && (scenarioAligned || liqSeqAligned)) {
    setupCleanliness = Math.min(100, setupCleanliness + 7);
  } else if (trendAligned) {
    setupCleanliness = Math.min(100, setupCleanliness + 4);
  }

  // ── Weighted 5-component readiness scoring ─────────────────────────────────
  // Weights: structure 0.30, liquidity 0.25, location 0.20, FVG quality 0.15, momentum 0.10
  // Each component scored 0–100; weighted sum = raw score
  // Score compression: diminishing returns above 70 prevent easy stacking to 100

  // Structure component (0–100): BOS/CHOCH recency + stop behind structure
  const _smc = ctx.smc;
  const _lastBOSRecent  = !!(_smc?.lastBOS  && _smc.lastBOS.candleIdx  >= ctx.candles.length - 8);
  const _lastCHOCHRecent= !!(_smc?.lastCHOCH && _smc.lastCHOCH.candleIdx >= ctx.candles.length - 8);
  const _biasAligned    = _smc?.bias === (dir === 'long' ? 'bullish' : 'bearish');
  let structureScore = 30;
  // Boost structure score when phase aligns with setup
  if (phaseAligned) structureScore += 8;
  if (displacementConflict) structureScore = Math.max(0, structureScore - 15);
  // Trend conflict further reduces structural confidence
  if (trendConflict && !_tCompressed) structureScore = Math.max(0, structureScore - 10);
  if (stackedConflict)                structureScore = Math.max(0, structureScore - 15);
  // Trend aligned with structure = additional quality signal
  if (trendAligned && _tStr >= 0.5)  structureScore = Math.min(100, structureScore + 8);
  const _hasMajorBOS   = _lastBOSRecent   && _smc?.lastBOS?.strength   === 'major';
  const _hasMajorCHOCH = _lastCHOCHRecent && _smc?.lastCHOCH?.strength === 'major';
  const _hasMinorBOS   = _lastBOSRecent   && !_hasMajorBOS;
  const _hasMinorCHOCH = _lastCHOCHRecent && !_hasMajorCHOCH;

  // Major structure: full credit. Minor structure: ~25% credit (not 50%).
  // Prevents minor structure from meaningfully inflating readiness.
  if (_hasMajorBOS)   structureScore += 30;
  else if (_hasMinorBOS)   structureScore += 8;   // minor BOS: 8 (was 15)

  if (_hasMajorCHOCH) structureScore += 20;
  else if (_hasMinorCHOCH) structureScore += 5;   // minor CHOCH: 5 (was 10)

  if (_biasAligned)     structureScore += 15;
  if (ctx.smc?.biasStrength === 'major') structureScore += 10;
  if (stopQuality >= 70) structureScore += 15;

  // Cap: if only minor structure exists (no major BOS or CHOCH), cap at 60
  // Prevents minor structure from stacking with bias/stop to reach inflated scores
  if (!_hasMajorBOS && !_hasMajorCHOCH) {
    structureScore = Math.min(60, structureScore);
  }
  structureScore = Math.min(100, structureScore);

  // Liquidity component (0–100): sweeps, nearby zones, structural level proximity
  const _sweepActive = !!(ctx.smc?.recentSweep?.reclaimed);
  const _nearHighZone = ctx.zones.some(z => z.sig === 'High' && z.distPct < 0.4);
  let liqScore = 20;
  if (_sweepActive) liqScore += 40;
  if (_nearHighZone) liqScore += 25;
  if (ctx.smc?.sweepAndReverse) liqScore += 15;
  if (liqSeqAligned) liqScore += 10;  // liquidity sequence aligned = quality boost
  liqScore = Math.min(100, liqScore);

  // Location component (0–100): premium/discount + no conflict
  let locationScore = 30;
  if (!premiumDiscountConflict && ctx.smc?.premiumDiscount) {
    const pd = ctx.smc.premiumDiscount;
    if ((dir === 'long' && pd.zone === 'discount') || (dir === 'short' && pd.zone === 'premium')) {
      locationScore += 45; // aligned = strong positive
    } else if (pd.zone === 'equilibrium') {
      locationScore += 15; // neutral
    }
  } else if (premiumDiscountConflict) {
    locationScore -= 20;   // wrong side — hard penalty
  }
  if (!regimeConflict) locationScore += 15;
  locationScore = Math.min(100, Math.max(0, locationScore));

  // FVG quality component (0–100): based on fvgWeight from smcEngine
  type FVGExt = FairValueGap & { fvgWeight?: number; fvgGrade?: string };
  const activeFVG: FVGExt | null = (ctx.smc?.fvgBelow as FVGExt | null) ??
                                    (ctx.smc?.fvgAbove as FVGExt | null);
  // Conservative: missing fvgWeight treated as degraded (0.4), not absent
  const fvgW = activeFVG
    ? ((activeFVG.fvgWeight ?? 0.4))  // ?? 0.4 = degraded fallback, not optimistic
    : null;
  const fvgQualScore = fvgW !== null
    ? Math.round(fvgW * 90)   // fresh=90, mild=63, degraded=36, invalid=0
    : 30;                      // no FVG = neutral baseline

  // Momentum component (0–100): RVOL + AI conviction
  let momentumScore = 20;
  const rvol = ctx.regime?.rvol ?? 0;
  if (rvol >= 2.0)  momentumScore += 45;
  else if (rvol >= 1.5) momentumScore += 30;
  else if (rvol >= 1.2) momentumScore += 15;
  else if (rvol < 0.8)  momentumScore -= 10;
  if (aiConviction >= 72) momentumScore += 30;
  else if (aiConviction >= 60) momentumScore += 18;
  else if (aiConviction >= 45) momentumScore += 8;
  momentumScore = Math.min(100, Math.max(0, momentumScore));

  // Weighted sum
  const rawScore = (
    structureScore * 0.30 +
    liqScore       * 0.25 +
    locationScore  * 0.20 +
    fvgQualScore   * 0.15 +
    momentumScore  * 0.10
  );

  // Score compression: diminishing returns above 70
  // Formula: if raw > 70, excess is scaled by 0.4 → prevents easy stacking near 100
  const readinessScore = rawScore <= 70
    ? rawScore
    : 70 + (rawScore - 70) * 0.4;

  // ── Apply stacked conflict / quad-alignment to weighted score ─────────────
  // Adjustments to weighted components (structureScore already patched above).
  // Stacked conflict also impacts momentum and location scores to propagate the penalty.
  let adjustedReadiness = readinessScore;
  if (stackedConflict)  adjustedReadiness = Math.max(0, adjustedReadiness - 12);
  if (quadAligned)      adjustedReadiness = Math.min(100, adjustedReadiness + 6);

  // ── Readiness tiers (A+/A/B/C aligned) ───────────────────────────────────
  // 90–100 → urgent (A+), 80–89 → ready (A), 70–79 → good (B), <70 → interesting (C)
  let readiness: ReadinessTier;
  if      (adjustedReadiness >= 75 && aiConviction >= 70 && !trapConflict && !stackedConflict && stopQuality >= 65) readiness = 'urgent';
  else if (adjustedReadiness >= 62 && hasCompleteLevel && confluenceCount >= 3)                readiness = 'ready';
  else if (adjustedReadiness >= 46)                                                             readiness = 'good';
  else                                                                                          readiness = 'interesting';

  // Exhaustiveness: ensures all ReadinessTier values are handled in display consumers.
  // Adding a new tier value will cause compile errors here until the switch is updated.
  const _readinessCheck: Record<ReadinessTier, true> = {
    interesting: true, good: true, ready: true, urgent: true,
  };
  void _readinessCheck[readiness];

  // ── Uncertainty note ─────────────────────────────────────────────────────
  const uncertaintyNote = buildUncertaintyNote(
    ctx, hasCompleteLevel, aiConviction, trapConflict, regimeConflict
  );

  // ── Missing for upgrade ──────────────────────────────────────────────────
  const missingForReady: string[] = [];
  const missingForUrgent: string[] = [];

  if (!hasCompleteLevel)    missingForReady.push('Define entry, stop, and target levels');
  if (stopQuality < 60)     missingForReady.push('Improve stop placement (structural level)');
  if (confluenceCount < 4)  missingForReady.push('More confirming signals needed');
  if (trapConflict)         missingForReady.push('Resolve active trap conflict');
  if (trendConflict && !_tCompressed && !stackedConflict) missingForReady.push(`Setup fights ${_tDir} EMA trend (strength ${(_tStr * 100).toFixed(0)}%)`);
  if (stackedConflict)      missingForReady.push(`Stacked conflict: fresh ${_dispDir} displacement + ${_tDir} EMA trend (${(_tStr * 100).toFixed(0)}%) both oppose this setup`);
  if (regimeConflict)       missingForReady.push('Regime is not aligned with setup direction');

  if (aiConviction < 70)    missingForUrgent.push('AI conviction ≥ 70%');
  if (confluenceCount < 7)  missingForUrgent.push(`${7 - confluenceCount} more confluent signals`);
  if (expectedR === null || expectedR < 2.5) missingForUrgent.push('R/R ≥ 2.5');

  // Emit diagnostics for high-readiness setups
  if (readiness === 'ready' || readiness === 'urgent') {
    logReadinessUpgrade(setup.type, setup.type, 'good', readiness);
  }

  return {
    expectedR,
    stopQuality,
    targetQuality,
    setupCleanliness,
    readiness,
    readinessScore,
    hasCompleteLevel,
    trapConflict,
    regimeConflict,
    premiumDiscountConflict,
    confluenceCount,
    uncertaintyNote,
    missingForReady,
    missingForUrgent,
  };
}

// ─── Batch evaluate all setups for a context ─────────────────────────────────

export function evaluateAllSetups(
  ctx:          SymbolContext,
  aiConviction: number = 0
): Array<{ setup: TradeSetup; risk: RiskAssessment }> {
  return ctx.setups.map(setup => ({
    setup,
    risk: evaluateSetupRisk(setup, ctx, aiConviction),
  })).sort((a, b) => b.risk.readinessScore - a.risk.readinessScore);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractFirstNumber(str: string): number | null {
  if (!str) return null;
  const m = str.match(/\d+\.?\d*/);
  if (!m) return null;
  const n = parseFloat(m[0]);
  return isNaN(n) ? null : n;
}

function buildUncertaintyNote(
  ctx:              SymbolContext,
  hasCompleteLevel: boolean,
  aiConviction:     number,
  trapConflict:     boolean,
  regimeConflict:   boolean
): string {
  const notes: string[] = [];

  if (!ctx.dataQuality.hasRealData)
    notes.push('Sample/CSV data — not live market conditions');
  if (!ctx.dataQuality.hasSufficientBars)
    notes.push(`Only ${ctx.candleCount} bars — analysis quality limited`);
  if (!ctx.dataQuality.hasGreeks)
    notes.push('Options OI only — gamma analysis is inferred, not exact');
  if (!hasCompleteLevel)
    notes.push('Incomplete level definition — R/R cannot be calculated');
  if (aiConviction === 0)
    notes.push('No AI thesis available — deterministic signals only');
  if (trapConflict)
    notes.push('Active trap in setup direction — elevated failure risk');
  if (regimeConflict)
    notes.push('Regime does not support this setup direction');

  if (!notes.length)
    return 'Data quality is sufficient for this analysis.';

  return notes.join(' · ');
}
